/**
 * Debug Logger for tracking API calls and detecting infinite loops
 * Enable by setting localStorage.setItem('DEBUG_API_CALLS', 'true') in browser console
 */

interface CallRecord {
  url: string;
  method: string;
  timestamp: number;
  stack: string;
  count: number;
}

class DebugLogger {
  private calls: Map<string, CallRecord> = new Map();
  private enabled: boolean = false;
  private originalFetch: typeof fetch | null = null;
  private loopThreshold = 5; // Alert if same call happens more than 5 times in 10 seconds
  private windowMs = 10000;

  constructor() {
    // Check if debug mode is enabled
    if (typeof window !== 'undefined') {
      this.enabled = localStorage.getItem('DEBUG_API_CALLS') === 'true';
      if (this.enabled) {
        this.install();
        console.log('🔍 [DebugLogger] API call tracking ENABLED');
        console.log('🔍 [DebugLogger] To disable: localStorage.removeItem("DEBUG_API_CALLS") and refresh');
      }
    }
  }

  install() {
    if (this.originalFetch) return; // Already installed

    this.originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';

      this.trackCall(url, method);

      return this.originalFetch!.call(window, input, init);
    };

    console.log('🔍 [DebugLogger] Fetch interceptor installed');
  }

  private trackCall(url: string, method: string) {
    const now = Date.now();
    const key = `${method}:${url}`;

    // Get stack trace
    const stack = new Error().stack || '';
    const relevantStack = stack
      .split('\n')
      .slice(3, 8) // Skip Error, trackCall, fetch wrapper
      .join('\n');

    const existing = this.calls.get(key);

    if (existing && (now - existing.timestamp) < this.windowMs) {
      existing.count++;
      existing.timestamp = now;

      // Check for loop
      if (existing.count === this.loopThreshold) {
        console.error(`🚨 [DebugLogger] LOOP DETECTED: ${method} ${url}`);
        console.error(`🚨 Called ${existing.count} times in ${this.windowMs/1000}s`);
        console.error('🚨 Call stack:', relevantStack);
        console.error('🚨 Original stack:', existing.stack);

        // Also log to a global array for easy inspection
        (window as any).__debugLoopDetected = (window as any).__debugLoopDetected || [];
        (window as any).__debugLoopDetected.push({
          url,
          method,
          count: existing.count,
          stack: relevantStack,
          originalStack: existing.stack,
          time: new Date().toISOString()
        });
      } else if (existing.count > this.loopThreshold && existing.count % 10 === 0) {
        console.error(`🚨 [DebugLogger] LOOP CONTINUES: ${method} ${url} - ${existing.count} calls`);
      }
    } else {
      this.calls.set(key, {
        url,
        method,
        timestamp: now,
        stack: relevantStack,
        count: 1
      });
    }

    // Log every call when verbose mode is on
    if (localStorage.getItem('DEBUG_API_VERBOSE') === 'true') {
      console.log(`📡 [API] ${method} ${url.substring(0, 80)}...`);
    }

    // Clean old entries
    this.cleanup(now);
  }

  private cleanup(now: number) {
    for (const [key, record] of this.calls.entries()) {
      if (now - record.timestamp > this.windowMs * 2) {
        this.calls.delete(key);
      }
    }
  }

  getStats() {
    const stats: Record<string, { count: number; lastCall: string }> = {};
    for (const [key, record] of this.calls.entries()) {
      stats[key] = {
        count: record.count,
        lastCall: new Date(record.timestamp).toISOString()
      };
    }
    return stats;
  }

  getLoops() {
    return (window as any).__debugLoopDetected || [];
  }

  reset() {
    this.calls.clear();
    (window as any).__debugLoopDetected = [];
    console.log('🔍 [DebugLogger] Stats reset');
  }
}

// Create singleton instance
export const debugLogger = new DebugLogger();

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as any).__debugLogger = debugLogger;
  (window as any).__enableDebugLogging = () => {
    localStorage.setItem('DEBUG_API_CALLS', 'true');
    console.log('Debug logging enabled. Refresh the page to activate.');
  };
  (window as any).__enableVerboseLogging = () => {
    localStorage.setItem('DEBUG_API_CALLS', 'true');
    localStorage.setItem('DEBUG_API_VERBOSE', 'true');
    console.log('Verbose debug logging enabled. Refresh the page to activate.');
  };
  (window as any).__disableDebugLogging = () => {
    localStorage.removeItem('DEBUG_API_CALLS');
    localStorage.removeItem('DEBUG_API_VERBOSE');
    console.log('Debug logging disabled. Refresh the page to deactivate.');
  };
  (window as any).__getApiStats = () => debugLogger.getStats();
  (window as any).__getLoops = () => debugLogger.getLoops();
}
